import { useState } from "react";
import { useDispatch } from "react-redux";

import { Button, Popover } from "antd";
import { ALL_PRODUCT_TYPES } from "constants/chart.constants";
import styled from "styled-components";
import { getProductIcon } from "utils/chart/getProductIcon";

import { BaseButton, BaseTooltip } from "components/base";

import { useChartsContext } from "../../../contexts";
import { useChartEntities } from "../../../hooks/charts";
import { IProductType } from "../../../models/chart";
import { updateChartByPath } from "../../../store/features";
import ProductHeader from "../components/ProductHeader";

export interface CumProductSection {
  feature: "pressureChartSettings" | "totalFluidChartSettings";
  title: string;
  products: IProductType[];
  // Shown for a stale or unknown saved key; matches the feature's default entity.
  defaultProduct: IProductType;
  testIdPrefix: string;
}

export const PRESSURE_CUM_SECTION: CumProductSection = {
  feature: "pressureChartSettings",
  title: "Pressure",
  products: [
    ALL_PRODUCT_TYPES.Oil,
    ALL_PRODUCT_TYPES.Gas,
    ALL_PRODUCT_TYPES.Water,
    ALL_PRODUCT_TYPES.CGR,
    ALL_PRODUCT_TYPES.GOR
  ],
  defaultProduct: ALL_PRODUCT_TYPES.Oil,
  testIdPrefix: "pressure"
};

export const TOTAL_FLUID_CUM_SECTION: CumProductSection = {
  feature: "totalFluidChartSettings",
  title: "Total Fluid",
  products: [ALL_PRODUCT_TYPES.Oil, ALL_PRODUCT_TYPES.TotalFluid],
  defaultProduct: ALL_PRODUCT_TYPES.TotalFluid,
  testIdPrefix: "totalFluidCum"
};

// Picks which product's cum goes on the rate-cum X axis, one section per product type in the
// chart (an MPC can hold both pressure and Total Fluid) behind a single trigger.
// Note: In order to be able to select the pressure charts in the app you have to set your
// data source to "Private" and then set the timestep to daily, this will cause the pressure
// charts to be available for selection.
export default function PressureChartOptions({
  sections = [PRESSURE_CUM_SECTION]
}: {
  sections?: CumProductSection[];
}) {
  const { id } = useChartsContext();
  const entities = useChartEntities(id);
  const dispatch = useDispatch();
  const isSingleSection = sections.length === 1;

  const selected = sections.map((section) => {
    const key = entities[section.feature].properties.product;
    return section.products.find((p) => p.key === key) ?? section.defaultProduct;
  });
  const summary = selected.map((item) => item?.label).join(" | ");

  const [popoverVisible, setPopoverVisible] = useState(false);

  const handleProductSelect = (section: CumProductSection, product: IProductType) => {
    dispatch(
      updateChartByPath({
        id,
        path: `features.${section.feature}.properties.product`,
        value: product.key
      })
    );
    // With several sections the popover stays open so each can be set in one go.
    if (isSingleSection) setPopoverVisible(false);
  };

  const content = (
    <ProductContainer>
      {sections.map((section, i) => (
        <ProductHeader
          key={section.feature}
          header={isSingleSection ? "Product" : section.title}>
          {section.products.map((product) => (
            <ProductButton
              data-testId={`${section.testIdPrefix}ProductButton${product.key}`}
              key={product.key}
              ellipses={false}
              onClick={() => {
                handleProductSelect(section, product);
              }}
              appearance=""
              className={`${product.key === selected[i]?.key ? "isSelected" : null}`}>
              {getProductIcon(product.label)}
              <span> {product.label}</span>
            </ProductButton>
          ))}
        </ProductHeader>
      ))}
    </ProductContainer>
  );

  const tooltip = isSingleSection
    ? summary
    : sections.map((section, i) => `${section.title}: ${selected[i]?.label}`).join(", ");

  return (
    <Wrapper>
      <BaseTooltip text={tooltip}>
        <Popover
          content={content}
          placement="top"
          trigger="click"
          visible={popoverVisible}
          onVisibleChange={(visible) => setPopoverVisible(visible)}
          overlayClassName="group-by-popover">
          <StyledButton
            data-testId={
              isSingleSection
                ? `${sections[0].testIdPrefix}ProductSelector`
                : "cumProductSelector"
            }
            onClick={() => setPopoverVisible(!popoverVisible)}>
            {summary}
          </StyledButton>
        </Popover>
      </BaseTooltip>
    </Wrapper>
  );
}

const ProductButton = styled(BaseButton)`
  min-height: 60px;
  height: auto;
  border-radius: 4px;
  color: #041c2c;
  background-color: unset;
  font-weight: var(--fontWeightMedium);
  font-size: 12px;

  &.isSelected {
    background: rgba(var(--color-shadow-rgb), 0.3);
    color: var(--color-primary);

    svg {
      color: var(--color-primary);
    }
  }

  span {
    margin-top: 0;
  }

  .label {
    line-height: 18px;
    margin: 0 2px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  &:hover {
    background: rgba(var(--color-shadow-rgb), 0.3);

    svg {
      color: var(--color-primary);
    }
  }

  &.isSelected:hover {
    background: rgba(var(--color-shadow-rgb), 0.9);

    svg {
      color: var(--color-primary);
    }
  }

  svg {
    min-height: 40px;
    color: #a2aaad;
    transition: color var(--duration-short);
  }
`;

const ProductContainer = styled.div`
  width: 375px;
  padding-bottom: 25px;
  max-height: 90vh;
  overflow-y: auto; /* Enable vertical scrolling */

  .ant-tabs-top > .ant-tabs-nav::before {
    border-bottom: 1px solid #d9e1e2;
  }

  .ant-tabs-nav {
    margin: 0;
  }

  .ant-tabs-nav-list {
    display: flex;
    justify-content: center;
    padding: 0 25px;
  }

  .ant-tabs-tab .ant-tabs-tab-btn {
    font-weight: bold;
    font-size: 1.4rem;
    padding: 0 0px;
    margin: 0;
  }
`;

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledButton = styled(Button)`
  max-width: 180px;
  width: 180px;

  & > span {
    width: 100%;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }
`;
